/**
 * Browser-history mirror for the mobile back gesture.
 *
 * WHY THIS EXISTS. App.tsx says it out loud: "The screen switch is a single
 * `screen` field. There is no router." That is still true and still deliberate —
 * but it meant browser history never grew, so on a phone the OS back gesture
 * left the site from the very first tap, whether the user was three sheets deep
 * or on Tonight. This module closes that gap WITHOUT a router: it mirrors the
 * app's *logical nav depth* into `history.state` and translates `popstate` into
 * "close the topmost open level".
 *
 * NOT A PORT. `design/Games Tonight Mobile.dc.html` was a single-page mock with
 * no history integration at all, so there are no prototype lines to cite here —
 * this is post-prototype production behaviour. The register entry is
 * docs/PORTING-NOTES.md, "Introduced with the mobile back-gesture fix".
 *
 * THE URL NEVER CHANGES. Every `pushState` / `replaceState` below passes a state
 * object and nothing else — no url argument, ever. Deep links, share targets and
 * the dev gallery's `/dev/gallery` path (dev/Gallery.tsx owns the URL there, and
 * main.tsx skips init for it) are therefore untouched.
 *
 * THE LEVEL STACK. `navLevels()` derives the levels sitting ABOVE the root,
 * bottom → top. The root is depth 0 — `login`, `reset` or `mine` — and we
 * never own an entry for it, so a back gesture at depth 0 leaves the site, which
 * is what a user expects from a home screen. Above it:
 *
 *   tab      the current tab when it is not MY GAMES (or, on `detail`, the tab
 *            `from` points back at)                              → nav('mine')
 *   detail   `screen === 'detail'`                               → goBack()
 *   sheets   in visual stacking order: the App.tsx overlay block is `z 80` for
 *            everything except MoveSheet at `z 85`, and among the z80 sheets the
 *            DOM mount order in App.tsx decides who paints on top.
 *
 * Deliberately NOT levels: `drag` / `dragOver` (a pointer gesture — the pointer
 * cancels it), the `invite` panel (inline on Crew, not an overlay), toasts, the
 * tooltip, `rmArm` / `ptRmArm` (arming is not navigation) and `fltPrDrag`.
 *
 * `overlayOpen()` reads the same stack for a second consumer: `lib/scroll.ts`
 * freezes the page behind whatever is painted over it, and derives "is anything
 * over the page?" from these levels rather than re-listing the sheets.
 *
 * THE HISTORY INVARIANT. Every entry we own carries `{ qd: <depth> }` and
 * nothing else, and our entries cover a contiguous depth range `[base .. cur]`.
 * `base` is the depth we found at init and only ever decreases. Entries carry
 * depth *only* — there is no serialized UI state and therefore no forward
 * restore: going forward into an entry whose UI is gone is bounced back rather
 * than guessed at (see `popstate` below). Storing enough to restore a sheet
 * would mean storing crew data in history, which the store already owns and the
 * server can invalidate at any moment.
 *
 * TWO RULES THAT LOOK LIKE PARANOIA AND ARE NOT:
 *
 *  1. Nothing is ever pushed from inside the `popstate` handler. Chrome's
 *     back-trap intervention exists precisely to punish that, and it also fights
 *     the iOS swipe animation mid-gesture.
 *  2. Nothing is pushed before the document has seen a real tap or key. Init
 *     runs at module scope, before React, and `boot()` may then restore a deep
 *     screen (`store/boot.ts` keeps `detail`, or lands `reset`) with no user
 *     gesture behind it. Pushing there would build a back-trap out of a page the
 *     user never navigated, and Chrome skips no-activation entries on a back
 *     gesture anyway. So until the first interaction a depth increase *replaces*
 *     and raises `base`: a reload or a boot restore behaves like a fresh entry
 *     and one back gesture leaves the site.
 *
 * TESTABILITY. `navLevels` is pure and takes a structural slice, and the
 * effectful half takes an injected `HistoryEnv`, so `history.test.ts` runs in
 * the repo's NODE test environment with a fake store and a fake history — no
 * jsdom, no real `window`.
 */

import type { Screen } from '../lib/types.ts';
import type { QdStore } from './types.ts';

// ---------------------------------------------------------------------------
// the level stack — pure
// ---------------------------------------------------------------------------

/** The store fields the level stack reads. Structural, so a test can pass a plain object. */
export type NavState = Pick<
  QdStore,
  | 'screen'
  | 'from'
  | 'fltOpen'
  | 'wnOpen'
  | 'ptDet'
  | 'ptAddOpen'
  | 'ptBrowseOpen'
  | 'ptTrackedOpen'
  | 'ptSyncOpen'
  | 'ptAsk'
  | 'gotyPickOpen'
  | 'friendsOpen'
  | 'capOpen'
  | 'pwOpen'
  | 'emOpen'
  | 'swOpen'
  | 'ownModal'
  | 'ccOpen'
  | 'sheet'
  | 'rSheet'
  | 'scId'
  | 'trkOpen'
  | 'sdId'
  | 'wmId'
  | 'askId'
  | 'addId'
  | 'schDay'
  | 'schGame'
  | 'schNomOpen'
>;

/** The store actions a level closes itself with — the same ones its own ✕ calls. */
export type NavActions = Pick<
  QdStore,
  | 'nav'
  | 'goBack'
  | 'toggleFltOpen'
  | 'closeWhatsNew'
  | 'closePtDet'
  | 'closePtAdd'
  | 'closePtBrowse'
  | 'closePtTracked'
  | 'closePtSync'
  | 'closePtAsk'
  | 'closeGotyPick'
  | 'closeFriends'
  | 'closeCap'
  | 'closePw'
  | 'closeEm'
  | 'closeSw'
  | 'closeOwn'
  | 'closeCc'
  | 'closeSheet'
  | 'rSheetClose'
  | 'closeScore'
  | 'closeTrack'
  | 'closeSend'
  | 'closeWatch'
  | 'closeAsk'
  | 'closeAdd'
  | 'schBack'
  | 'schCloseGame'
  | 'schNomClose'
>;

export type NavLevelKind =
  | 'tab'
  | 'detail'
  | 'flt'
  | 'wn'
  | 'ptDet'
  | 'ptAdd'
  | 'ptBrowse'
  | 'ptTracked'
  | 'ptSync'
  | 'ptAsk'
  | 'gotyPick'
  | 'friends'
  | 'cap'
  | 'pw'
  | 'em'
  | 'sw'
  | 'own'
  | 'cc'
  | 'move'
  | 'rSheet'
  | 'score'
  | 'track'
  | 'send'
  | 'wm'
  | 'ask'
  | 'add'
  | 'schDay'
  | 'schNom'
  | 'schGame';

export interface NavLevel {
  readonly kind: NavLevelKind;
  /** Closes exactly this level. Called with a FRESH `getState()` — a close can cascade. */
  readonly close: (a: NavActions) => void;
}

/**
 * Shared singletons rather than fresh closures per call: `navLevels` runs on
 * every store change (zustand notifies on every `set`, including the 5 s ticker
 * and every `dragOver` during a drag), so the only allocation per pass is the
 * array itself.
 */
const LEVEL: { readonly [K in NavLevelKind]: NavLevel } = {
  tab: { kind: 'tab', close: (a) => a.nav('mine') },
  detail: { kind: 'detail', close: (a) => a.goBack() },
  flt: { kind: 'flt', close: (a) => a.toggleFltOpen(false) },
  wn: { kind: 'wn', close: (a) => a.closeWhatsNew() },
  ptDet: { kind: 'ptDet', close: (a) => a.closePtDet() },
  ptAdd: { kind: 'ptAdd', close: (a) => a.closePtAdd() },
  ptBrowse: { kind: 'ptBrowse', close: (a) => a.closePtBrowse() },
  ptTracked: { kind: 'ptTracked', close: (a) => a.closePtTracked() },
  ptSync: { kind: 'ptSync', close: (a) => a.closePtSync() },
  ptAsk: { kind: 'ptAsk', close: (a) => a.closePtAsk() },
  gotyPick: { kind: 'gotyPick', close: (a) => a.closeGotyPick() },
  friends: { kind: 'friends', close: (a) => a.closeFriends() },
  cap: { kind: 'cap', close: (a) => a.closeCap() },
  pw: { kind: 'pw', close: (a) => a.closePw() },
  em: { kind: 'em', close: (a) => a.closeEm() },
  sw: { kind: 'sw', close: (a) => a.closeSw() },
  own: { kind: 'own', close: (a) => a.closeOwn() },
  cc: { kind: 'cc', close: (a) => a.closeCc() },
  move: { kind: 'move', close: (a) => a.closeSheet() },
  rSheet: { kind: 'rSheet', close: (a) => a.rSheetClose() },
  score: { kind: 'score', close: (a) => a.closeScore() },
  track: { kind: 'track', close: (a) => a.closeTrack() },
  send: { kind: 'send', close: (a) => a.closeSend() },
  wm: { kind: 'wm', close: (a) => a.closeWatch() },
  ask: { kind: 'ask', close: (a) => a.closeAsk() },
  add: { kind: 'add', close: (a) => a.closeAdd() },
  schDay: { kind: 'schDay', close: (a) => a.schBack() },
  schNom: { kind: 'schNom', close: (a) => a.schNomClose() },
  schGame: { kind: 'schGame', close: (a) => a.schCloseGame() },
};

/**
 * MY GAMES is the root, so it is absent here on purpose; `detail` is its own
 * level. The root moved from Tonight on 2026-09-03 with the landing screen
 * (DESIGN-CHANGELOG "MY GAMES: the page about you"): a back gesture closes
 * towards wherever a sign-in lands, and that is the hub now.
 */
const TAB_SCREENS: ReadonlySet<Screen> = new Set<Screen>([
  'tonight',
  'discover',
  'interests',
  'goty',
  'playtests',
  'crew',
  'profile',
  // MY GAMES' full-screen sub-page (AA phase 2): back returns to the hub.
  'backlog',
  'scores',
  'recs',
  'monitoring',
  'manage',
  'member',
  // The round page is pushed from the board (3039); a back gesture returns
  // there and keeps your seat, exactly like `◂ BACK TO THE BOARD`.
  'round',
  // SCHEDULE (3071) is a tab; a night of it is its own level above (`schDay`).
  'schedule',
  'schedDay',
]);

/**
 * The logical stack above the root, bottom → top. `navLevels(s).length` is the
 * depth; the LAST element is what a back gesture closes.
 */
export function navLevels(s: Readonly<NavState>): NavLevel[] {
  const out: NavLevel[] = [];

  // `login` and `reset` render without header or bottom nav (App.tsx frame
  // rules) and no sheet is reachable from either. A flag left set by a previous
  // session must not make the back gesture look like it has somewhere to go, so
  // this returns before reading any of them.
  // `landing` joins them: a crewless account has no header, no bottom nav and
  // nothing to go back to either (phase C; phase D draws the screen).
  if (
    s.screen === 'login' ||
    s.screen === 'reset' ||
    s.screen === 'recover' ||
    s.screen === 'landing' ||
    s.screen === 'startCrew'
  ) {
    return out;
  }

  // On `detail` the tab underneath is whatever `from` points at — that is where
  // `goBack()` lands, so it is the level below.
  const tab = s.screen === 'detail' ? s.from : s.screen;
  if (TAB_SCREENS.has(tab)) out.push(LEVEL.tab);
  // A night of the SCHEDULE sits over the month: back returns to the month,
  // exactly like `◂ BACK TO SCHEDULE`.
  if (tab === 'schedDay' && s.schDay) out.push(LEVEL.schDay);
  if (s.screen === 'detail') out.push(LEVEL.detail);

  // Sheets, bottom → top. Order is the paint order of the App.tsx overlay block
  // ("z 80 · move 85 · …"): the z80 sheets in DOM mount order, MoveSheet last
  // because z85 puts it above all of them.
  // The phone's NOMINATE sheet over the round page (3039) sits at z 75 — UNDER
  // the filter sheet it can open (the mirror's own ordering) — so it registers
  // before `flt`: back closes the filters onto the covers, not the covers under
  // the filters.
  if (s.rSheet) out.push(LEVEL.rSheet);
  if (s.fltOpen) out.push(LEVEL.flt);
  if (s.wnOpen) out.push(LEVEL.wn);
  if (s.ptDet !== '') out.push(LEVEL.ptDet);
  if (s.ptAddOpen) out.push(LEVEL.ptAdd);
  if (s.ptBrowseOpen) out.push(LEVEL.ptBrowse);
  if (s.ptTrackedOpen) out.push(LEVEL.ptTracked);
  if (s.ptSyncOpen) out.push(LEVEL.ptSync);
  // Last of the playtest block, matching its mount order: the confirm opens
  // OVER the detail sheet (its CTA is one of the four that can raise it), so a
  // back gesture has to close the question before the sheet that asked it.
  if (s.ptAsk !== '') out.push(LEVEL.ptAsk);
  // Registered: the picker is a full-height list a member can scroll a long way
  // into, and being unable to back out of one is the worst case of the gap that
  // registration exists to close.
  if (s.gotyPickOpen) out.push(LEVEL.gotyPick);
  // The phone's full-screen FRIENDS view, for the same reason as the picker
  // above and one more: it paints an OPAQUE `--gt-bg` over the whole viewport
  // including the bottom nav, so an unregistered one would be a surface with no
  // visible way out at all. Registration is also what makes `overlayOpen` true
  // for it, which is what freezes the detail screen scrolling underneath.
  if (s.friendsOpen) out.push(LEVEL.friends);
  // Above the FRIENDS view: on phone the editor opens from the rail BEHIND that
  // sheet, but it is a dialog over whatever is showing, and a back gesture that
  // closed the page under an open form would lose what was typed.
  if (s.capOpen !== 0) out.push(LEVEL.cap);
  // The CHANGE PASSWORD sheet — a form over the Profile tab; a back gesture
  // that skipped it would close the page under what was being typed. Mounted
  // after `cap` in App.tsx, so it registers after it.
  if (s.pwOpen) out.push(LEVEL.pw);
  if (s.emOpen) out.push(LEVEL.em);
  if (s.swOpen) out.push(LEVEL.sw);
  // TRANSFER OWNERSHIP / DELETE CREW. Both are armed forms — a chosen member,
  // a typed crew name — so a back gesture that closed the page underneath
  // would drop what was entered on the way to an irreversible action.
  if (s.ownModal) out.push(LEVEL.own);
  // The custom color picker — same rule as `pw`: a form (an armed pick) over
  // the Profile tab. Mounted after it in App.tsx.
  if (s.ccOpen) out.push(LEVEL.cc);
  // YOUR SCORE and TRACK A GAME (3060): forms over BACKLOG, GAME DETAIL or the
  // hub. Mounted last in both shells, so they register last — a back gesture
  // closes the dialog before the page it was opened from.
  if (s.scId !== 0) out.push(LEVEL.score);
  if (s.trkOpen) out.push(LEVEL.track);
  // RECOMMEND (3062): a form — a picked roster, a typed note — over detail,
  // the hub or RECOMMENDATIONS. Mounted after the two above in both shells.
  if (s.sdId !== 0) out.push(LEVEL.send);
  // TELL ME WHEN (3063): a draft form over detail, BACKLOG or MONITORING.
  if (s.wmId !== 0) out.push(LEVEL.wm);
  // CALL IT (AA phase 5): the ask that follows an add, over whatever added.
  if (s.askId !== 0) out.push(LEVEL.ask);
  // ADD IT (docs/ADD-IT.md): the where-does-it-go question over Discover.
  // Never open at the same time as `ask` — it closes before the add runs —
  // so the order between the two is moot; it sits last among the dialogs.
  if (s.addId !== 0) out.push(LEVEL.add);
  // SCHEDULE's two dialogs (3071), only while their night is the page — the
  // game modal survives a trip to GAME PAGE ▸ so BACK lands on it again, and
  // must not count as an overlay over the detail screen meanwhile. NOMINATE
  // and the game modal never stack; the order is moot.
  if (s.screen === 'schedDay' && s.schDay && s.schNomOpen) out.push(LEVEL.schNom);
  if (s.screen === 'schedDay' && s.schDay && s.schGame !== 0) out.push(LEVEL.schGame);
  if (s.sheet !== null) out.push(LEVEL.move);

  return out;
}

/**
 * Is anything painted OVER the page right now? The scroll lock in
 * `lib/scroll.ts` asks, so that a scroll over a sheet's backdrop — or a flick
 * that started inside a sheet and ran past its end — cannot move the screen
 * behind it.
 *
 * Derived from `navLevels` rather than listing the sheets a second time. The two
 * questions are not the same one ("what does back close?" vs "what blocks the
 * page?"), but their answers move together, and a list that has to be kept in
 * step with another list eventually is not: the next sheet gets both from here.
 * The one difference is deliberate: `tab` and `detail` are levels but not
 * overlays — they ARE the page.
 *
 * `tagOpen` used to be the second exception, an overlay that was not a level.
 * The tag picker is a section of the filter panel now, so it rides `fltOpen`,
 * which has always been a registered level — one list instead of two, which is
 * what this comment kept asking for.
 */
export function overlayOpen(s: Readonly<NavState>): boolean {
  // `schDay` is a PAGE too — a night of the SCHEDULE pushed over the month —
  // and counting it would freeze the very page it is (3071).
  return navLevels(s).some((l) => l.kind !== 'tab' && l.kind !== 'detail' && l.kind !== 'schDay');
}

// ---------------------------------------------------------------------------
// the injected environment
// ---------------------------------------------------------------------------

/**
 * The slice of `History` this module uses. Note what is missing: the `url`
 * argument. `pushState(data, unused)` is the whole surface, so no call site can
 * change the address bar even by accident.
 */
export interface HistoryLike {
  readonly state: unknown;
  pushState(data: unknown, unused: string): void;
  replaceState(data: unknown, unused: string): void;
  go(delta: number): void;
  scrollRestoration?: ScrollRestoration;
}

export interface HistoryEnv {
  readonly history: HistoryLike;
  /** Registers the single `popstate` handler; the argument is the entry's state. */
  addPopStateListener(fn: (state: unknown) => void): void;
  /**
   * Registers a ONE-SHOT "the document has been touched" handler.
   *
   * This is the honest seam for rule 2 in the header: production listens for
   * `pointerdown` / `keydown` once, a test calls the registered function
   * directly. Nothing else in the module reads user activation.
   */
  addFirstInteractionListener(fn: () => void): void;
}

/** The two `useQd` members this module needs, narrowed so a fake store can satisfy them. */
export interface HistoryStore {
  getState: () => NavState & NavActions;
  /** The listener takes no arguments — depth is always re-derived from `getState()`. */
  subscribe: (listener: () => void) => () => void;
}

/** Production wiring. Kept here so main.tsx stays a two-line hook-up. */
export function browserHistoryEnv(): HistoryEnv {
  return {
    history: window.history,
    addPopStateListener: (fn) => {
      window.addEventListener('popstate', (ev) => fn(ev.state));
    },
    addFirstInteractionListener: (fn) => {
      const once = (): void => {
        window.removeEventListener('pointerdown', once, true);
        window.removeEventListener('keydown', once, true);
        fn();
      };
      // Capture phase: a `stopPropagation` anywhere in the tree must not be able
      // to hide the interaction from us and strand the app in replace-only mode.
      window.addEventListener('pointerdown', once, true);
      window.addEventListener('keydown', once, true);
    },
  };
}

// ---------------------------------------------------------------------------
// the sync
// ---------------------------------------------------------------------------

/** The active teardown, or `null` when the module has not been initialised. */
let active: (() => void) | null = null;

/**
 * Mirrors nav depth into browser history and closes levels on `popstate`.
 *
 * Idempotent: a second call while a sync is live is a no-op, so an accidental
 * double import (or a dev-server hot reload of main.tsx) cannot end up with two
 * subscriptions fighting over the same history entries.
 */
export function initHistorySync(store: HistoryStore, env: HistoryEnv): void {
  if (active) return;

  const h = env.history;

  /** Lowest depth we own an entry for. Only ever decreases. */
  let base = 0;
  /** `go()` calls whose `popstate` acknowledgement has not come back yet. */
  let expectedPops = 0;
  /** A reconcile is already queued for this tick. */
  let queued = false;
  /** See rule 2 in the header. Init runs before paint, so this cannot start true. */
  let interacted = false;

  const depth = (): number => navLevels(store.getState()).length;

  /**
   * The depth the current entry claims. An entry that is not ours — the page the
   * user came from, or one a browser restored without our state — reads as
   * `base`, which makes every branch below treat it as "nothing of ours here".
   */
  const entryDepth = (): number => {
    const st: unknown = h.state;
    if (typeof st === 'object' && st !== null && 'qd' in st) {
      const qd = st.qd;
      if (typeof qd === 'number' && Number.isFinite(qd)) return qd;
    }
    return base;
  };

  const mark = (qd: number): { qd: number } => ({ qd });

  /**
   * Bring history in line with the store. Reads `h.state` exactly once, so a
   * test can count reconcile passes by counting state reads.
   */
  const reconcile = (): void => {
    // A `go()` is in flight: `h.state` still points at the entry we are leaving,
    // so any decision made now would be made on stale ground. Drop this pass —
    // the acknowledgement re-runs one unconditionally, which subsumes it.
    if (expectedPops > 0) return;

    const target = depth();
    const cur = entryDepth();

    if (target > cur) {
      // Rule 2: no push before the document has been touched. Relabel the entry
      // we are standing on and raise the floor instead, so back still exits.
      if (!interacted) {
        h.replaceState(mark(target), '');
        base = target;
        return;
      }
      // `pushState` is synchronous — no acknowledgement to wait for.
      for (let n = cur + 1; n <= target; n++) h.pushState(mark(n), '');
      return;
    }

    if (target < cur) {
      // Consume only entries we own. Anything below `base` belongs to whatever
      // the user was looking at before this app and is not ours to spend.
      const n = Math.min(cur - target, cur - base);
      if (n > 0) {
        // One `popstate` fires per `go()` call, however many entries it skips.
        expectedPops += 1;
        h.go(-n);
        return;
      }
      // Nothing left to consume and we are still above target: the base itself
      // is too high (a boot-restored deep screen collapsing under it). Relabel.
      h.replaceState(mark(target), '');
      base = target;
      return;
    }

    // target === cur — entries carry depth and nothing else, so there is nothing
    // to freshen. This is the settled state and the common case.
  };

  /** Coalesces the several `set()`s a single tap can fire into one reconcile pass. */
  const schedule = (): void => {
    if (queued) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      reconcile();
    });
  };

  const onPopState = (state: unknown): void => {
    if (expectedPops > 0) {
      // Our own `go()` coming back. The store is already where we want it — this
      // must NOT close anything, or an in-app close would close twice.
      //
      // Known benign race: a user swipe that lands while our `go()` is in flight
      // is swallowed here as the acknowledgement. The reconcile below then sees
      // whatever entry we actually ended up on and re-runs the shrink, so the
      // invariant heals itself one pass later.
      expectedPops -= 1;
      // Always re-run once the last one is home: the store may have moved while
      // the `go()` was in flight, and a pass that changes nothing is free.
      if (expectedPops === 0) schedule();
      return;
    }

    // A real back / forward gesture.
    let d = base;
    if (typeof state === 'object' && state !== null && 'qd' in state) {
      const qd = state.qd;
      if (typeof qd === 'number' && Number.isFinite(qd)) d = qd;
    }
    // Landing below the floor means the floor was wrong; it only ever drops.
    base = Math.min(base, d);
    // The user just drove the history themselves, which is activation enough:
    // from here on a depth increase is a real navigation and gets a real push.
    interacted = true;

    const app = depth();

    if (d < app) {
      // Close top-down. `getState()` is re-read every iteration because a close
      // can cascade (`nav()` clears `sheet`, `goBack()` goes through `nav()`).
      for (let i = app; i > d; i--) {
        const s = store.getState();
        const levels = navLevels(s);
        if (levels.length <= d) break;
        const top = levels[levels.length - 1];
        if (!top) break;
        top.close(s);
      }
      // The resulting store change schedules a reconcile of its own, which will
      // find `target === cur` and settle. This one is belt-and-braces for the
      // case where nothing actually changed.
      schedule();
      return;
    }

    if (d > app) {
      // Forward into an entry whose UI state no longer exists. Entries carry
      // depth only (see the invariant), so there is nothing to restore and
      // guessing would be worse than refusing: bounce straight back. Never a
      // `pushState` here — that is rule 1.
      expectedPops += 1;
      h.go(app - d);
      return;
    }

    // d === app — the gesture crossed an entry that already matches the store.
  };

  // Every popstate here is a same-document level close, and the layout only
  // changes once the store reacts a microtask later — so a browser-restored
  // scroll offset would always be measured against the page we are leaving.
  if (h.scrollRestoration !== undefined) h.scrollRestoration = 'manual';

  // Claim the entry we were loaded on. `boot()` has not run yet and may move the
  // screen; the subscription below reconciles that like any other change (under
  // rule 2, so it replaces rather than pushes).
  const d0 = depth();
  h.replaceState(mark(d0), '');
  base = d0;

  env.addFirstInteractionListener(() => {
    interacted = true;
  });
  env.addPopStateListener(onPopState);
  active = store.subscribe(schedule);
}

/** Test seam — drops the subscription and lets `initHistorySync` run again. */
export function resetHistorySync(): void {
  active?.();
  active = null;
}
